import { type ChangeEvent, type KeyboardEvent, useState } from 'react';
import { Stack, type StackProps, type SxProps } from '@mui/material';
import { MHMTextField } from '../MHM/MHMTextField';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMButton } from '../MHM/MHMButton';
import { ErrorText } from '../MHM/MHMTypography';
import { labelsSelector } from '../../../models/selectors/company_selectors';
import { useRecoilValue } from 'recoil';

export type LabelInputProps = StackProps & {
  defaultValue?: string;
  textFieldLabel?: string;
  textFieldSx?: SxProps;
  onSave(newLabel: string): void | boolean | Promise<boolean | void>;
};

export function LabelInput(props: LabelInputProps) {
  const { defaultValue, textFieldLabel, textFieldSx, onSave, ...rest } = props;
  const { t } = useTranslation();
  const labels = useRecoilValue(labelsSelector);
  const [newLabelText, setNewLabelText] = useState(defaultValue ?? '');
  const [labelValidationError, setLabelValidationError] = useState('');

  const isNewLabelValid = Boolean(newLabelText) && !labelValidationError;
  const hasLabelTextChanged = newLabelText !== defaultValue;

  function handleNewLabelChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setNewLabelText(value);

    if (
      value === t('No messages') ||
      value === t('Low battery') ||
      value === t('Weather mode')
    ) {
      setLabelValidationError('Label not allowed!');
      return;
    }

    if (!/^(?=.*[^\s])[a-zA-Z0-9\s.,/\-_!@£$%)(?+=]+$/.test(value)) {
      setLabelValidationError(
        'Labels cannot be empty and can contain only letters, numbers and some special characters (- _ . / ! % ? £ $ + ,)',
      );
      return;
    }

    if (labels.includes(value) && value !== defaultValue) {
      setLabelValidationError(t('Label already exists'));
      return;
    }

    setLabelValidationError('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    event.stopPropagation();
    if (event.key !== 'Enter') {
      return;
    }

    createLabel();
  }

  async function createLabel() {
    if (!isNewLabelValid || !hasLabelTextChanged) {
      return;
    }

    const keepLabelText = await onSave(newLabelText);
    if (keepLabelText) {
      return;
    }
    setNewLabelText(defaultValue ?? '');
  }

  return (
    <Stack direction="row" gap={2} {...rest}>
      <MHMTextField
        label={textFieldLabel ?? t('Create new label')}
        value={newLabelText}
        size="small"
        error={Boolean(labelValidationError)}
        helperText={
          <ErrorText
            sx={{ textWrap: 'wrap', minWidth: '100%', maxWidth: 'min-content' }}
          >
            {t(labelValidationError)}
          </ErrorText>
        }
        onChange={handleNewLabelChange}
        onKeyDown={handleKeyDown}
        sx={textFieldSx}
      />
      <MHMButton
        size="medium"
        disabled={!isNewLabelValid || !hasLabelTextChanged}
        onClick={createLabel}
        sx={{ height: '40px' }}
      >
        {t('Save')}
      </MHMButton>
    </Stack>
  );
}
